import Flag, { FlagGroup } from '@atlaskit/flag';
import Avatar from '@atlaskit/avatar';
import { useIntl } from 'react-intl-next';
import { useCallback, useEffect } from 'react';
import { auth, type AuthError } from '@atlaskit/outbound-auth-flow-client';

import { createRender, ConnectorProvidersMapping } from './types';
import { ManualMessageViewedEvent } from '@post-office/placement-contexts/message-viewed-component';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';

/**
 * ⚠️  CRITICAL SAFETY WARNING FOR POST OFFICE MESSAGES ⚠️
 *
 * Messages in Post Office can go live and reach customers very easily, particularly
 * with implicit message creation. Before merging this code, ensure you have:
 *
 * 1. ✅ Set up a Statsig filter to control message visibility
 *    📖 https://developer.atlassian.com/platform/post-office/working-with-post-office/technical-deep-dives/pre-defined-filters/#statsig-filter
 *
 * 2. ✅ Set up an environment filter to prevent accidental production deployment
 *    📖 https://developer.atlassian.com/platform/post-office/working-with-post-office/technical-deep-dives/pre-defined-filters/#environment-filter
 *
 * 3. ✅ If using implicit messages, configure them properly in implicit-messages
 *    📖 https://developer.atlassian.com/platform/post-office/message-template/implicit-messages/#what-are-implicit-messages
 *
 * 4. ✅ Verify that you're not rendering anything you dont want customers to see.
 *    📖 https://bitbucket.org/atlassian/atlassian-frontend-monorepo/pull-requests/213556
 *
 * Remember: Test thoroughly in staging environments before enabling in production!
 */

export const render = createRender(({ onDismissed, messageInstanceId, listOfConnectorApps }) => {
	const { formatMessage } = useIntl();
	const { messageInstanceViewed, messageInstanceClicked } = useMessageInstanceEvent();
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const connectorApp = listOfConnectorApps[0];

	// Track message viewed event
	useEffect(() => {
		if (messageInstanceViewed && connectorApp) {
			void messageInstanceViewed({
				appsShown: connectorApp.providerType,
			});
		}
	}, [messageInstanceViewed, connectorApp]);

	if (!connectorApp) {
		return null;
	}

	// Loop over ConnectorProvidersMapping to find matching providerType and extract logo
	const connectorLogo = Object.values(ConnectorProvidersMapping).find(
		(item) => item.providerType === connectorApp.providerType,
	)?.logo;

	const handleConnectClick = useCallback(async () => {
		try {
			void messageInstanceClicked({
				messageEventType: 'clicked',
				clickedElement: {
					clickIdentifier: 'connectNow',
					clickedElement: 'link',
				},
				action: {
					actionType: 'open',
				},
				additionalMetadataFromFrontend: {
					appsShown: connectorApp.providerType,
				},
			});

			auth(connectorApp.outboundAuthUrl).then(
				() => {
					// Fire analytics event for successful connection
					fireAnalyticsEvent({
						action: 'connected',
						actionSubject: 'applicationAccount',
						eventType: 'track',
						attributes: {
							appsShown: connectorApp.providerType,
							status: 'unauthorized',
						},
						tags: ['media'],
					});
				},
				(err: AuthError) => {
					// Fire analytics event for failed connection
					fireAnalyticsEvent({
						action: 'connectFailed',
						actionSubject: '3rdPartyConnectorFlag',
						eventType: 'track',
						attributes: {
							appsShown: connectorApp.providerType,
							errorType: err.type ?? null,
							status: 'unauthorized',
						},
					});

					// Fire analytics event for auth window closed
					if (err.type === 'auth_window_closed') {
						fireAnalyticsEvent({
							action: 'closed',
							actionSubject: 'window',
							actionSubjectId: 'confluence-3p-connector-nudge',
							eventType: 'ui',
							attributes: {
								appsShown: connectorApp.providerType,
							},
							tags: ['media'],
						});
					}
				},
			);
		} catch {
			// If messageInstanceClicked or auth throws synchronously, we still want to dismiss the flag
		} finally {
			void onDismissed(messageInstanceId);
		}
	}, [
		messageInstanceClicked,
		onDismissed,
		messageInstanceId,
		connectorApp.providerType,
		fireAnalyticsEvent,
		connectorApp.outboundAuthUrl,
	]);

	const handleLearnMoreClick = useCallback(async () => {
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'learnMore',
				clickedElement: 'link',
			},
			action: {
				actionType: 'open',
			},
			additionalMetadataFromFrontend: {
				appsShown: connectorApp.providerType,
			},
		});
	}, [messageInstanceClicked, connectorApp.providerType]);

	const handleDismiss = useCallback(async () => {
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'dismiss',
				clickedElement: 'button',
				buttonType: 'secondary',
			},
			action: {
				actionType: 'dismiss',
			},
			additionalMetadataFromFrontend: {
				appsShown: connectorApp.providerType,
			},
		});
		void onDismissed(messageInstanceId);
	}, [messageInstanceClicked, onDismissed, messageInstanceId, connectorApp.providerType]);

	return (
		<ManualMessageViewedEvent>
			<FlagGroup onDismissed={handleDismiss}>
				<Flag
					id="confluence-3p-connector-nudge-flag-id"
					title={formatMessage(
						{
							id: 'confluence-3p-connector-nudge.title',
							defaultMessage: 'Connect {providerName}',
							description: 'Title of the 3rd party connector nudge',
						},
						{ providerName: connectorApp.normalizedAppName },
					)}
					description={formatMessage(
						{
							id: 'confluence-3p-connector-nudge.description',
							defaultMessage:
								"You've been using {providerName}. Connect it to get richer previews and better search.",
							description:
								'Description explaining the benefits of connecting the 3rd party provider',
						},
						{ providerName: connectorApp.normalizedAppName },
					)}
					icon={
						<Avatar
							appearance="circle"
							src={connectorLogo}
							borderColor={'rgba(0, 0, 0, 0)'}
							size="small"
							label={connectorApp.normalizedAppName}
						/>
					}
					actions={[
						{
							content: formatMessage({
								id: 'confluence-3p-connector-nudge.action.connect',
								defaultMessage: 'Connect now',
								description: 'Button text to connect the 3rd party provider',
							}),
							onClick: handleConnectClick,
						},
						...(connectorApp.providerLearnMoreUrl
							? [
									{
										content: formatMessage({
											id: 'confluence-3p-connector-nudge.action.learnMore',
											defaultMessage: 'Learn more',
											description: 'Button text to learn more about connecting',
										}),
										onClick: handleLearnMoreClick,
										href: connectorApp.providerLearnMoreUrl,
										target: '_blank',
									},
								]
							: []),
					]}
				/>
			</FlagGroup>
		</ManualMessageViewedEvent>
	);
});

export default render;
